import { curveLinear } from '@visx/curve';
import { LinearGradient } from '@visx/gradient';
import { AreaClosed, Circle, LinePath } from '@visx/shape';
import type { ScaleLinear, ScaleTime } from '@visx/vendor/d3-scale';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment } from 'react';
import type { ChartValues, Data } from '@/types/chart';

type CharLineProps = {
  data: ChartValues;
  chartColor: { default: string; hover: string };
  dashStyles: string;
  hovered: number | string | null | undefined;
  xScale: ScaleTime<number, number, never>;
  yScale: ScaleLinear<number, number, never>;
  accessor: { getValue: (d: Data) => number; getDate: (d: Data) => Date };
  margin: number;
  circlePos: { x: number; y: number } | null;
  showGradient: boolean;
};

const Line = ({
  data,
  chartColor,
  dashStyles,
  hovered,
  xScale,
  yScale,
  accessor,
  margin,
  circlePos,
  showGradient,
}: CharLineProps) => {
  const nullIndices = data.data.map((d, i) => (accessor.getValue(d) === null ? i : -1)).filter((index) => index !== -1);

  const lineSegments = nullIndices.map((nullIndex, i) => {
    const start = i === 0 ? 0 : nullIndices[i - 1] + 1;
    const end = nullIndex;
    return data.data.slice(start, end);
  });

  const lastStart = nullIndices[nullIndices.length - 1] + 1;
  lineSegments.push(data.data.slice(lastStart));

  return (
    <Fragment key={data.name}>
      {lineSegments.map((lineSegment, i) => {
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional line segment, never reordered
          <Fragment key={i}>
            {/* How cicle if we only have one value. (Happens start of year, when only one data point for granularity is available) */}
            {lineSegment.length === 1 && (
              <Circle
                // biome-ignore lint/suspicious/noArrayIndexKey: positional line segment, never reordered
                key={`circle--${i}-${lineSegment[0].date}`}
                cx={xScale(accessor.getDate(lineSegment[0]))}
                cy={yScale(lineSegment[0].value)}
                r={3}
                style={{
                  strokeWidth: hovered === data.id ? 2 : 1.5,
                  fill: hovered === data.id ? chartColor.hover : chartColor.default,
                  opacity: hovered !== data.id && hovered !== undefined ? 0.2 : 1,
                }}
              />
            )}

            {/* Gradient fill below line path */}
            <AnimatePresence>
              {showGradient && hovered === data.id && lineSegment.length > 0 && (
                <motion.g
                  clipPath='url(#graphAreaClipPath)'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  // transition={{ duration: 0.1 }}
                >
                  <LinearGradient
                    id={`area-gradient-${i}`}
                    from={chartColor.default}
                    to={chartColor.default}
                    fromOpacity={0.1}
                    toOpacity={0}
                  />
                  <AreaClosed
                    data={lineSegment}
                    x={(d) => xScale(accessor.getDate(d))}
                    y={(d) => yScale(accessor.getValue(d))}
                    yScale={yScale}
                    fill={`url(#area-gradient-${i})`}
                    curve={curveLinear}
                  />
                </motion.g>
              )}
            </AnimatePresence>

            {/* Line */}
            <LinePath
              curve={curveLinear}
              data={lineSegment}
              x={(d) => xScale(accessor.getDate(d))}
              y={(d) => yScale(accessor.getValue(d))}
            >
              {({ path }) => (
                <path
                  d={path(lineSegment) || ''}
                  strokeDasharray={dashStyles}
                  style={{
                    strokeDasharray: dashStyles,
                    strokeWidth: hovered === data.id ? 2 : 1.5,
                    stroke: hovered === data.id ? chartColor.hover : chartColor.default,
                    opacity: hovered !== data.id && hovered !== undefined ? 0.2 : 1,
                  }}
                  fill='transparent'
                />
              )}
            </LinePath>

            {/* Data point dots, only when this line is hovered */}
            {hovered === data.id &&
              lineSegment.length > 1 &&
              lineSegment.map((d, dotIndex) => (
                <Circle
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional data point within segment, never reordered
                  key={`dot-${i}-${dotIndex}-${d.date}`}
                  cx={xScale(accessor.getDate(d))}
                  cy={yScale(accessor.getValue(d))}
                  r={2.5}
                  style={{
                    fill: chartColor.default,
                    stroke: 'white',
                    strokeWidth: 1,
                    opacity: 0.8,
                  }}
                />
              ))}

            <AnimatePresence>
              {hovered === data.id && circlePos !== null && (
                <Fragment key={`circle-${data.id}`}>
                  <defs>
                    <filter
                      id='shadowBlur'
                      x='-50%'
                      y='-50%'
                      width='200%'
                      height='200%'
                    >
                      <feGaussianBlur stdDeviation='4' />
                    </filter>
                  </defs>
                  <motion.circle
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    cx={circlePos.x - (margin || 0)}
                    cy={circlePos.y + 2}
                    stroke='white'
                    strokeWidth={2}
                    r={6}
                    fill={'black'}
                    fillOpacity={0.4}
                    filter='url(#shadowBlur)'
                  />
                  <motion.circle
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    cx={circlePos.x - (margin || 0)}
                    cy={circlePos.y}
                    stroke='white'
                    strokeWidth={1.5}
                    r={4}
                    fill={chartColor.default}
                  />
                </Fragment>
              )}
            </AnimatePresence>
          </Fragment>
        );
      })}
    </Fragment>
  );
};

export default Line;
